import {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { CORRELATION_HEADER, newCorrelationId } from '@shared/utils';

import { HTTP_ERROR_NETWORK, HttpError } from './HttpError';

export interface AuthBridge {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  refresh(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }>;
  setTokens(tokens: { accessToken: string; refreshToken: string }): void;
  clearSession(): void;
  onSessionExpired(): void;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;

function isNonRetryableErrorCode(code: string | undefined): boolean {
  return code === 'INVALID_AUDIENCE' || code === 'FORBIDDEN' || code === 'USER_INACTIVE';
}

export function installInterceptors(client: AxiosInstance, auth: AuthBridge): AxiosInstance {
  client.interceptors.request.use((config) => {
    const headers = ensureAxiosHeaders(config);

    if (!headers.has(CORRELATION_HEADER)) {
      headers.set(CORRELATION_HEADER, newCorrelationId());
    }

    const token = auth.getAccessToken();
    if (token !== null && token.length > 0) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!(error instanceof AxiosError)) {
        throw new HttpError({
          statusCode: HTTP_ERROR_NETWORK,
          message: 'Erro inesperado na requisição.',
          cause: error,
        });
      }

      const cfg = (error.config ?? null) as RetriableConfig | null;
      const status = error.response?.status ?? HTTP_ERROR_NETWORK;
      const correlationId = readCorrelationId(error);

      if (status === 401 && cfg !== null && cfg._retry !== true) {
        const isLoginCall = cfg.url?.includes('/auth/login') === true;
        if (isLoginCall) {
          throw mapAxiosToHttpError(error, status, correlationId);
        }

        const isRefreshCall = cfg.url?.includes('/auth/refresh') === true;
        if (isRefreshCall) {
          auth.clearSession();
          auth.onSessionExpired();
          throw mapAxiosToHttpError(error, status, correlationId);
        }

        const errorCode = (error.response?.data as { error?: { code?: string } } | undefined)?.error
          ?.code;
        if (isNonRetryableErrorCode(errorCode)) {
          throw mapAxiosToHttpError(error, status, correlationId);
        }

        const refreshToken = auth.getRefreshToken();
        if (refreshToken === null || refreshToken.length === 0) {
          auth.clearSession();
          auth.onSessionExpired();
          throw mapAxiosToHttpError(error, status, correlationId);
        }

        try {
          refreshPromise ??= auth.refresh(refreshToken).finally(() => {
            refreshPromise = null;
          });
          const fresh = await refreshPromise;
          auth.setTokens(fresh);
          cfg._retry = true;
          ensureAxiosHeaders(cfg).set('Authorization', `Bearer ${fresh.accessToken}`);
          return await client.request(cfg);
        } catch (refreshErr) {
          auth.clearSession();
          auth.onSessionExpired();
          throw new HttpError({
            statusCode: 401,
            message: 'Sessão expirada. Faça login novamente.',
            ...(correlationId !== undefined ? { correlationId } : {}),
            cause: refreshErr,
          });
        }
      }

      throw mapAxiosToHttpError(error, status, correlationId);
    },
  );

  return client;
}

function ensureAxiosHeaders(config: InternalAxiosRequestConfig): AxiosHeaders {
  if (config.headers instanceof AxiosHeaders) {
    return config.headers;
  }
  const normalized = new AxiosHeaders(config.headers);
  config.headers = normalized;
  return normalized;
}

function readCorrelationId(error: AxiosError): string | undefined {
  const config = error.config;
  if (config !== undefined) {
    const headers = config.headers;
    if (headers instanceof AxiosHeaders) {
      const value = headers.get(CORRELATION_HEADER);
      if (typeof value === 'string') return value;
    } else if (headers !== undefined) {
      const record = headers as Record<string, unknown>;
      const value = record[CORRELATION_HEADER] ?? record[CORRELATION_HEADER.toLowerCase()];
      if (typeof value === 'string') return value;
    }
  }
  return undefined;
}

function mapAxiosToHttpError(
  error: AxiosError,
  statusCode: number,
  correlationId: string | undefined,
): HttpError {
  const serverPayload = error.response?.data;
  return new HttpError({
    statusCode,
    message: mapStatusToFriendlyMessage(statusCode, error),
    ...(correlationId !== undefined ? { correlationId } : {}),
    ...(serverPayload !== undefined ? { serverPayload } : {}),
    cause: error,
  });
}

function mapStatusToFriendlyMessage(status: number, error: AxiosError): string {
  if (status === HTTP_ERROR_NETWORK) {
    if (error.code === 'ECONNABORTED') return 'Tempo limite da requisição excedido.';
    return 'Falha de comunicação com o servidor.';
  }
  if (status === 401) return 'Não autorizado.';
  if (status === 403) return 'Acesso negado.';
  if (status === 404) return 'Recurso não encontrado.';
  if (status === 409) return 'Conflito: registro já existe.';
  if (status >= 500) return 'Erro no servidor. Tente novamente em instantes.';
  if (status >= 400) return 'Requisição inválida.';
  return 'Erro inesperado.';
}

export type { AxiosResponse } from 'axios';
